import * as React from 'react'
import { createRef, FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Field, Form, useFormikContext } from 'formik'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import { HelperText } from '../../../components/Settings/LeadAssignmentRule/LeadAssignmentRuleForm/atoms'
import { styles } from '../../../components/TopToolbar/useStyles'
import { DebouncedTextField } from '../../../components/Formik/DebouncedTextField'
import Box from '@mui/material/Box'
import {
  SearchFilterType,
  SearchInput,
  SearchResponse,
} from '@user/store/search/typings'
import { useHandlers } from './hooks/usehandlers'
import { useGetSearchResults } from './hooks/useGetSearchResults'
import { SearchResults } from '@user/features/GlobalSearch/SearchResults/SearchResults'
import { defaultSearchFilter, SearchFilters } from './filters/SearchFilters'
import { useSelector } from 'react-redux/es/hooks/useSelector'
import { selectCurrentUser } from '@user/store/authorization/userSlice'
import { useGetSalesTeamPositionsData } from '@user/store/authorization/hooks/useGetSalesTeamPositionsData'
import {
  ISalesTeam,
  selectAllSalesTeams,
  useGetSalesTeamListOnMount,
} from '@user/store/salesTeam'
import { selectAllRecentlyVisitedRecords } from '@user/store/globalSearch'
import Button from '@mui/material/Button'
import SearchToolTip from '../SearchToolTip'
import { useTheme } from '@mui/material/styles'
import { inputBaseClasses } from '@mui/material/InputBase'
import { OnSearchAddressClick } from '../ResultItem/typings'

interface Props {
  onClose?: () => void
  onCustomizeDrawerOpen: (search: string) => void
  onSearchAddressClick?: OnSearchAddressClick
}

const maxSearchRecords = 50

export const TopSearch: FC<Props> = (props) => {
  const { onClose, onCustomizeDrawerOpen } = props
  const recentSearchData = useSelector(selectAllRecentlyVisitedRecords)

  const ref = createRef<HTMLInputElement>()
  const { icon, ...classes } = styles

  const [filters, setFilters] = useState<SearchFilterType>(defaultSearchFilter)

  const placeholder =
    'Search by Name/Company Name, Phone, Address, Location Number, or Email'
  const {
    values: { topSearch },
  } = useFormikContext<SearchInput>()

  const positionDataStatus = useGetSalesTeamPositionsData()
  useGetSalesTeamListOnMount()

  const salesTeams: ISalesTeam[] = useSelector(selectAllSalesTeams)

  const currentUser = useSelector(selectCurrentUser)
  const { searchStatus, searchData } = useGetSearchResults(topSearch)

  const { onClickClear, onKeyDown } = useHandlers({
    ref,
    onClose,
    topSearch,
  })

  const onFilterChange = useCallback(
    (filters: SearchFilterType) => {
      setFilters(filters)
    },
    [setFilters],
  )

  const invalidText = useMemo(
    () => topSearch?.trim().length > 0 && topSearch?.trim().length < 3,
    [topSearch],
  )

  const filteredSearchData: SearchResponse = useMemo(() => {
    const data =
      filters && Object.values(filters).includes(true)
        ? searchData?.searchList.filter((item) => {
            if (
              filters.closed !== !!item.closedDate ||
              (filters.myRecords && item.ownerId !== currentUser.userId)
            ) {
              return false
            }

            if (!filters.lead && !filters.opportunity && !filters.pendingLead)
              return true

            if (
              (filters.lead && item.entityType === 'Lead') ||
              (filters.opportunity && item.entityType === 'Opportunity') ||
              (filters.pendingLead && item.entityType === 'DraftLead')
            ) {
              return true
            }

            return false
          })
        : searchData?.searchList.filter((item) => !item.closedDate)

    const topItems = data?.slice(0, maxSearchRecords)
    return { searchList: topItems ?? [], count: topItems?.length ?? 0 }
  }, [filters, searchData, currentUser])

  const showOnlyCurrentUserRecs = useMemo(() => {
    if (currentUser.isAdmin) return false

    if (currentUser?.salesTeamPositionsData?.salesTeamPositions?.length)
      return false

    const userTeams = currentUser?.salesTeamPositionsData?.salesTeamIds

    const hasNonStructuredSharingTeam = salesTeams.some(
      (team) => userTeams?.includes(team.id) && !team.structuredSharing,
    )

    if (hasNonStructuredSharingTeam) return false

    return true
  }, [currentUser, salesTeams])

  useEffect(() => {
    ref.current?.focus()
  }, [ref])

  const onFilterDrawerOpen = useCallback(() => {
    onCustomizeDrawerOpen(topSearch || '')
  }, [topSearch, onCustomizeDrawerOpen])

  const dataAvailable =
    recentSearchData?.length > 0 || (searchData && searchData?.count > 0)
  const theme = useTheme()
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          flexDirection: 'column',
          overflow: 'hidden',
          padding: theme.spacing(2),
          paddingBottom: dataAvailable ? 0 : 2,
        }}
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault()
          }}
        >
          <Field
            name="topSearch"
            disabled={false}
            inputRef={ref}
            placeholder={placeholder}
            onKeyDown={onKeyDown}
            component={DebouncedTextField}
            debounceDelay={500}
            color="primary"
            variant="outlined"
            data-test-id="searchInputField"
            InputProps={{
              'data-test-id': `topSearchInput`,
              sx: {
                [`&.${inputBaseClasses.root}`]: {
                  ...classes.root,
                  ...classes.focused,
                },
                [`&.${inputBaseClasses.input}`]: {
                  ...classes.input,
                },
              },
              autoComplete: 'off',
              startAdornment: (
                <InputAdornment position="start">
                  <>
                    <SearchIcon sx={icon} data-test-id="searchIcon" />
                  </>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <>
                    <ClearIcon
                      onClick={onClickClear}
                      sx={icon}
                      data-test-id="searchIcon"
                    />
                  </>
                </InputAdornment>
              ),
            }}
          />

          {invalidText && (
            <Box ml={1}>
              <HelperText>Must type at least 3 characters.</HelperText>
            </Box>
          )}

          {searchData &&
            searchData?.count > 0 &&
            positionDataStatus === 'success' &&
            searchStatus === 'success' && (
              <>
                <SearchFilters
                  onChange={onFilterChange}
                  hideMyRecords={showOnlyCurrentUserRecs}
                  filters={filters}
                />
              </>
            )}
        </Form>

        <SearchResults
          onLocationClick={() => {
            if (onClose) onClose()
          }}
          onRowClick={() => {
            if (onClose) onClose()
          }}
          searchData={filteredSearchData}
          recentSearchData={recentSearchData || []}
          searchStatus={searchStatus}
          searchText={topSearch}
          onSearchAddressClick={props.onSearchAddressClick}
        />
      </Box>
      <Box
        sx={{
          paddingRight: theme.spacing(1.8),
          paddingLeft: theme.spacing(2.5),
          display: 'flex',
          height: '46px',
          bgcolor: theme.palette.background.default,
          borderTop: '1px solid #0000001F',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <SearchToolTip />
        <Button
          sx={{
            fontSize: '13px',
            lineHeight: '22px',
            letterSpacing: '0.46px',
            color: theme.palette.primary.main,
            fontFamily: 'Source Sans Pro Semibold',
          }}
          variant={'text'}
          data-test-id="customizeResultsDisplayBtn"
          onClick={onFilterDrawerOpen}
        >
          Customize Results Display
        </Button>
      </Box>
    </>
  )
}
